<script setup lang="ts">
import { VisXYContainer } from "@unovis/vue";
import { ChartCrosshair, ChartTooltipContent, componentToString, THEMES } from "~/components/ui/chart";
import { toChartVarName } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";

const props = defineProps<{ id: string }>();

/** 6a) Commands for context menu (optional) */
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "XY Container editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit this container",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

/** 7) Expose commands & properties for the resource editor / context menu */
defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "XY Container",
    icon: "lucide:box-select",
  },
});
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));
const containerHeight = computed(() => definitionData.value?.height || 400);
const features = inject("render-features", { editor: false });

// data from the provider (with lifecycle flags)
type InjectedReport = { data: Array<{ name: string }>; loading?: boolean; initialized?: boolean };
const report = inject<InjectedReport>("report", { data: [], loading: false, initialized: false });
const chartData = computed(() => (report.loading || !report.initialized ? [] : report.data));

const rawChartConfig = computed(() => {
  const config = definitionData.value?.chartConfig;
  if (typeof config === "string") {
    const code = config.trim();
    if (!code)
      return {};
    try {
      return new Function(`return (${code})`)();
    } catch {
      try {
        return new Function(code)();
      } catch (err) {
        console.error("Failed to eval chartConfig", err);
        return {};
      }
    }
  }
  return config && typeof config === "object" ? config : {};
});

const chartConfig = computed<ChartConfig>(() => {
  const mapped: ChartConfig = {};
  for (const [key, item] of Object.entries(rawChartConfig.value)) {
    const varName = toChartVarName(key);
    const hasTheme = !!item?.theme;
    const hasColor = !!item?.color;
    const color = (hasTheme || hasColor) && varName ? `var(--color-${varName})` : undefined;

    mapped[key] = {
      ...(item?.label ? { label: item.label } : {}),
      ...(item?.icon ? { icon: item.icon } : {}),
      ...(color ? { color } : {}),
    };
  }

  return mapped;
});

const chartStyleText = computed(() => {
  const entries = Object.entries(rawChartConfig.value);
  if (!entries.length)
    return "";

  const chartId = `chart-${props.id}`;

  return Object.entries(THEMES)
    .map(([theme, prefix]) => {
      const lines = entries
        .map(([key, item]) => {
          const varName = toChartVarName(key);
          if (!varName)
            return null;
          const themeColor = item?.theme?.[theme as keyof typeof THEMES] ?? item?.color;
          return themeColor ? `  --color-${varName}: ${themeColor};` : null;
        })
        .filter(Boolean)
        .join("\n");

      return lines ? `\n${prefix} [data-chart=${chartId}] {\n${lines}\n}\n` : "";
    })
    .filter(Boolean)
    .join("\n");
});

// optional: make tick helpers available for separate Axis blocks
const tickValues = computed(() => chartData.value.map((_, i) => i));
const tickFormat = (t: number) => chartData.value[t]?.name ?? "";
provide("xy-ticks", { tickValues, tickFormat });

// 3) Provide orientation from the XY container (NEW)
// Prefer a container-level orientation if you later add it;
// otherwise read it from the first chart-like child (stacked bar)
const children = computed(() => definitionData.value?.children ?? []);
const orientation = computed(() => {
  // If you later add orientation to the container itself:
  if (definitionData.value?.orientation)
    return definitionData.value.orientation;

  const chartChild = children.value.find((c: any) =>
    [
      ComponentDefinitionIds.UNOVIS_XY_STACKED_BAR,
      ComponentDefinitionIds.UNOVIS_XY_LINE,
      // Add other XY series components here as you add them
      // e.g., ComponentDefinitionIds.UNOVIS_XY_AREA, etc.
    ].includes(c.componentId)
  );

  return chartChild?.orientation ?? "vertical";
});
provide("xy-orientation", orientation);
</script>

<template>
  <div>
    <ClientOnly>
      <ChartContainer v-if="!features.editor" :id="props.id" :config="chartConfig" :height="containerHeight">
        <component v-if="chartStyleText" :is="'style'">
          {{ chartStyleText }}
        </component>
        <VisXYContainer :height="containerHeight">
          <slot />
        </VisXYContainer>
      </ChartContainer>
      <VisXYContainer v-else :height="containerHeight">
        <slot />
      </VisXYContainer>
    </ClientOnly>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import type { ChartConfig } from "~/components/ui/chart";
export default {
  editor: () => import("#bb/components/editor/unovis/container/xy.vue"),
  componentId: ComponentDefinitionIds.UNOVIS_XY_CONTAINER,
  id: "xycontainer",
  icon: "lucide:box-select",
  type: "XY Container",
  description:
    "XY Container is designed as a container that manages multiple XY Components at once.",
  default: { columnsSettings: { default: 1 }, children: [] },
  categories: ["UNOVIS"],
  order: 12004,
};
</script>
